import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import get from 'lodash/get';
import {
  screenXsMax,
  screenSmMin,
  screenSmMax,
  screenMdMin,
  screenMdMax,
  screenLgMin,
  screenLgMax,
  screenXlMin,
  spacer24,
  spacer40,
} from '@spotify-internal/encore-foundation';
import { DocumentTitle } from '@mrkt/features/document-title';
import { MeaningfulPaint } from '../../shared/components/MeaningfulPaint';
import {
  HOME_VIEW,
  VIEWER,
  useCurrentArtist,
  useCurrentArtistPermissions,
} from '../../features/artists';
import HomeResource from './HomeResource';
import StatSection from './StatSection';
import HeaderSection from './HeaderSection';
import StatsTopHeader from './StatSection/StatsTopHeader';
import CardGridLayout from './CardSection/CardGridLayout';
import { useHomeTracker } from './HomeTracker';
import {
  useViewport,
  Viewport,
  breakpointValues,
} from '../../shared/lib/useViewport';
import { FundraisingBanner } from '../ArtistProfile/Fundraising';
import { useArtistFundraisingPickSelfServeRollout } from '../ArtistProfile/utils';

const GridLayout = styled.div`
  display: grid;
  min-width: 0;

  @media (max-width: ${screenXsMax}) {
    grid-template-areas:
      'header'
      'listeners'
      'stats'
      'cards';
    grid-template-columns: 1fr;
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}) {
    grid-column-gap: ${spacer24};
    grid-template-areas:
      'header statsheader'
      'listeners stats'
      'cards stats';
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: ${screenMdMin}) and (max-width: ${screenMdMax}) {
    grid-column-gap: ${spacer40};
    grid-template-areas:
      'header statsheader'
      'listeners stats'
      'cards stats';
    grid-template-columns: 7fr 3fr;
  }

  @media (min-width: ${screenLgMin}) and (max-width: ${screenLgMax}) {
    grid-column-gap: ${spacer40};
    grid-template-areas:
      'header statsheader'
      'listeners stats'
      'cards stats';
    grid-template-columns: 3fr 1fr;
  }

  @media (min-width: ${screenXlMin}) {
    grid-column-gap: ${spacer40};
    grid-template-areas:
      'header statsheader'
      'listeners stats'
      'cards stats';
    grid-template-columns: 4fr 1fr;
  }
`;

export function useHomeResource(artistId) {
  useEffect(() => {
    return () => {
      HomeResource.invalidate(artistId);
    };
  }, [artistId]);

  try {
    return HomeResource.read(artistId);
  } catch (error) {
    if (error instanceof Error) HomeResource.invalidate(artistId);
    throw error;
  }
}

export function useIsHomeEnabledForCurrentUser() {
  const permissions = useCurrentArtistPermissions();
  return permissions.includes(VIEWER) || permissions.includes(HOME_VIEW);
}

export function ArtistHome(props) {
  const { history } = props;
  const artist = useCurrentArtist();
  const viewport = useViewport();
  const [activeStatsTabIndex, setActiveTabIndex] = useState(0);

  const onChangeStatsTab = useCallback(
    (e, index) => {
      e.preventDefault();
      setActiveTabIndex(index);
    },
    [setActiveTabIndex],
  );
  const isMobile = viewport === Viewport.XS;

  const response = useHomeResource(artist.id);

  const homeTracker = useHomeTracker();

  const latestDate = get(response, 'data.homestats.s4xinsightsapi.latestDate');

  const listenersData = get(
    response,
    'data.homestats.s4xinsightsapi.overview.listeners',
    {},
  );
  const streamsData = get(
    response,
    'data.homestats.s4xinsightsapi.overview.streams',
    {},
  );
  const followersData = get(
    response,
    'data.homestats.s4xinsightsapi.overview.followers',
    {},
  );

  const topSongsList = get(
    response,
    'data.homestats.s4xinsightsapi.recordingStatsTable.recordingStatsList',
    [],
  );

  const topPlaylistsList = get(
    response,
    'data.homestats.s4xinsightsapi.playlistCollection.dataList',
    [],
  );

  const cardData = get(response, 'data.homestats.cards.cardsList', []);

  const fundraisingPickSelfServeRolledOut = useArtistFundraisingPickSelfServeRollout();

  const statSectionWithProps = (
    <StatSection
      {...props}
      artist={artist}
      isMobile={isMobile}
      onChangeStatsTab={onChangeStatsTab}
      activeStatsTabIndex={activeStatsTabIndex}
      latestDate={latestDate}
      listeners={listenersData}
      streams={streamsData}
      followers={followersData}
      topSongs={topSongsList}
      topPlaylists={topPlaylistsList}
    />
  );

  return (
    <DocumentTitle title={`Home – ${artist.name} – Spotify for Artists`}>
      <MeaningfulPaint>
        {fundraisingPickSelfServeRolledOut ? null : <FundraisingBanner />}
        <GridLayout>
          <HeaderSection />

          {isMobile ? statSectionWithProps : null}

          <CardGridLayout
            cardData={cardData}
            artist={artist}
            history={history}
            viewport={breakpointValues[viewport]}
            tracker={homeTracker}
          />

          {!isMobile ? (
            <>
              <StatsTopHeader latestDate={latestDate} />
              {statSectionWithProps}
            </>
          ) : null}
        </GridLayout>
      </MeaningfulPaint>
    </DocumentTitle>
  );
}

ArtistHome.propTypes = {
  history: PropTypes.shape({}).isRequired,
};
